import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { Plus, ClipboardList } from "lucide-react";

const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};

export default function PortalRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    api.get("/portal/requests").then((r) => setRequests(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = filterStatus ? requests.filter((r) => r.status === filterStatus) : requests;

  return (
    <PortalLayout>
      <div data-testid="portal-requests-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>My Tasks</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{filtered.length} task{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <Link
            to="/portal/requests/new"
            data-testid="new-request-button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm"
            style={{ background: "#D97706", color: "#fff" }}
          >
            <Plus size={15} />
            New Task
          </Link>
        </div>

        <div className="flex gap-3 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
          >
            <option value="">All Statuses</option>
            {["pending_review", "in_progress", "on_hold", "completed", "cancelled"].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm mb-3" style={{ color: "#A8A29E" }}>No tasks yet</p>
              <Link to="/portal/requests/new" className="text-sm font-medium" style={{ color: "#D97706" }}>Submit your first task</Link>
            </div>
          ) : (
            filtered.map((req) => {
              const sc = statusColors[req.status] || { bg: "#F5F3EF", text: "#57534E" };
              return (
                <div
                  key={req.id}
                  data-testid={`portal-request-${req.id}`}
                  onClick={() => navigate(`/portal/requests/${req.id}`)}
                  className="bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: "#E7E5E4" }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium" style={{ color: "#292524" }}>{req.title}</p>
                      <p className="text-sm mt-1 text-sm leading-relaxed" style={{ color: "#57534E" }}>
                        {req.description?.slice(0, 100)}{req.description?.length > 100 && "..."}
                      </p>
                    </div>
                    <span className="ml-4 flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                      {req.status?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-xs" style={{ color: "#A8A29E" }}>
                    <span>{req.created_at?.slice(0, 10)}</span>
                    {req.priority && <span className="capitalize">Priority: {req.priority}</span>}
                    {req.time_estimate_hours && <span>Est: {req.time_estimate_hours}h</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
